import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import { ReactFacebookFailureResponse, ReactFacebookLoginInfo } from 'react-facebook-login';
import AppleSignIn from 'react-apple-signin-auth';
import { useGoogleLogin } from '@react-oauth/google';
import { SocialItem } from './SocialItem';
import { GoogleIcon } from '../../../commons/icons/Google';
import { ISocialAccount } from '../../../../models/UserInfo';
import { ApiError } from '../../../../models/ApiError';
import { socialsApi } from '../../../../services/apiServices/user/socials';
import { AppleConnectAuthError, AppleLoginResponse, TypeOfSocials } from '../../../../models/Socials';
import { TextButton, TextButtonClassNames } from '../../../commons/buttons/TextButton';
import { FacebookIcon } from '../../../commons/icons/Facebook';
import { SocialsProps } from '../../../../services/apiServices/user/authorization';
import { useCustomSnackbar } from '../../../../hooks/useCustomEnqueueSnackbar';
import { AppleIcon } from '../../../commons/icons/Apple';
import { appleClientId, appleClosePopupErrorMessage, googleLoginURL } from '../../../../constants/configuration';
import { parseJwt, getErrorMessage } from '../../../../utils';
import { useAppSelector } from '../../../../hooks/redux';

export interface UserSocialAccount {
  type: TypeOfSocials;
  icon: JSX.Element;
  label: string;
  connectButton: JSX.Element;
  handleUnlink: (social: TypeOfSocials, message: string) => void;
  isConnected: boolean;
  email: string | false;
  unlinkMessage: string;
}

export const SocialsSetting: FC = () => {
  const { t } = useTranslation();
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [unlinkSocialAPI] = socialsApi.useUnlinkMutation();
  const [connectSocialAPI] = socialsApi.useConnectMutation();
  const { userInfoState } = useAppSelector((state) => state.userInfoReducer);
  const redirectURI = window.location.href;
  const isFacebookAvailable = false;

  const handleDisconnectSocial = async (social: TypeOfSocials, message: string) => {
    try {
      await unlinkSocialAPI(social)
        .unwrap()
        .then(() => {
          customEnqueueSnackbar(message, 'warning');
        });
    } catch (err) {
      const mes = getErrorMessage(err);
      customEnqueueSnackbar(mes, 'error');
    }
  };

  const handleConnectSocial = async (props: SocialsProps): Promise<{ success: boolean } | ApiError> => {
    try {
      return await connectSocialAPI(props).unwrap();
    } catch (err) {
      const error = err as ApiError;
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
      return error;
    }
  };

  const handleAppleLogin = (res: AppleLoginResponse) => {
    const isOnline = 'authorization' in res;
    if (isOnline) {
      const jwt = parseJwt(res.authorization.id_token);
      handleConnectSocial({
        type: TypeOfSocials.Apple,
        socialUserId: jwt.sub,
        email: jwt.email,
      }).then((response) => {
        if ('success' in response) {
          customEnqueueSnackbar(t('snackbar.appleAccountWasConnected'), 'success');
        }
      });
    }
  };

  const handleAppleLoginError = (err: AppleConnectAuthError) => {
    if (err && err.error !== appleClosePopupErrorMessage) {
      customEnqueueSnackbar(err.error, 'error');
    }
  };

  const handleFacebookLogin = (res: ReactFacebookLoginInfo | ReactFacebookFailureResponse) => {
    const isSuccess = 'userID' in res;
    if (isSuccess) {
      handleConnectSocial({
        type: TypeOfSocials.Facebook,
        socialUserId: res.userID,
        email: res.email || '',
        accessToken: res.accessToken,
      }).then((response) => {
        if ('success' in response) {
          customEnqueueSnackbar(t('snackbar.facebookAccountWasConnected'), 'success');
        }
      });
    }
  };

  const setIsSocialConnected = (socials: ISocialAccount[], key: TypeOfSocials) => {
    return socials.some((social) => social.Network === key);
  };

  const findSocialEmail = (socials: ISocialAccount[], key: TypeOfSocials) => {
    const socialAccount = socials.find((social) => social.Network === key);
    if (socialAccount) {
      return socialAccount.Email;
    }
    return false;
  };

  const handleGoogleLoginError = (err: string | undefined) => {
    if (err) {
      customEnqueueSnackbar(err, 'error');
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      const info = await fetch(googleLoginURL, {
        headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
      });
      const userGoogleInfo = await info.json();
      const isOnline = 'email' in userGoogleInfo;
      if (isOnline) {
        await handleConnectSocial({
          type: TypeOfSocials.Google,
          socialUserId: userGoogleInfo.sub,
          email: userGoogleInfo.email,
        }).then((response) => {
          if ('success' in response) {
            customEnqueueSnackbar(t('snackbar.googleAccountWasConnected'), 'success');
          }
        });
      }
    },
    onError: (errorResponse) => handleGoogleLoginError(errorResponse.error_description),
  });

  const userSocialAccounts: UserSocialAccount[] = [
    {
      icon: <AppleIcon className="account__social-accounts__items__item__icon-label__icon" />,
      label: t('accountSettings.appleIdAccount'),
      connectButton: (
        <AppleSignIn
          authOptions={{
            clientId: appleClientId,
            scope: 'email name',
            redirectURI,
            state: 'state',
            nonce: 'nonce',
            usePopup: true,
          }}
          uiType="dark"
          className="apple-auth-btn"
          noDefaultStyle={false}
          buttonExtraChildren="Continue with Apple"
          onSuccess={(response) => handleAppleLogin(response)}
          onError={(error) => handleAppleLoginError(error)}
          skipScript={false}
          iconProp={{ style: { marginTop: '10px' } }}
          render={(renderProps) => (
            <TextButton
              label={t('buttons.connect')}
              handleClick={renderProps.onClick}
              className={TextButtonClassNames.PrimaryActive}
            />
          )}
        />
      ),
      handleUnlink: handleDisconnectSocial,
      isConnected:
        (userInfoState?.SocialAccounts && setIsSocialConnected(userInfoState.SocialAccounts, TypeOfSocials.Apple)) ||
        false,
      email:
        (userInfoState?.SocialAccounts && findSocialEmail(userInfoState.SocialAccounts, TypeOfSocials.Apple)) || false,
      type: TypeOfSocials.Apple,
      unlinkMessage: t('snackbar.appleAccountWasDisabled'),
    },
    {
      icon: <GoogleIcon className="account__social-accounts__items__item__icon-label__icon" />,
      label: t('accountSettings.googleAccount'),
      connectButton: (
        <TextButton
          label={t('buttons.connect')}
          handleClick={handleGoogleLogin}
          className={TextButtonClassNames.PrimaryActive}
        />
      ),
      handleUnlink: handleDisconnectSocial,
      isConnected:
        (userInfoState?.SocialAccounts && setIsSocialConnected(userInfoState.SocialAccounts, TypeOfSocials.Google)) ||
        false,
      email:
        (userInfoState?.SocialAccounts && findSocialEmail(userInfoState.SocialAccounts, TypeOfSocials.Google)) || false,
      type: TypeOfSocials.Google,
      unlinkMessage: t('snackbar.googleAccountWasDisabled'),
    },
    ...(isFacebookAvailable
      ? [
          {
            icon: <FacebookIcon className="account__social-accounts__items__item__icon-label__icon" />,
            label: t('accountSettings.facebookAccount'),
            connectButton: (
              <FacebookLogin
                appId={process.env.REACT_APP_FACEBOOK_CLIENT_ID || ''}
                callback={handleFacebookLogin}
                fields="name,email"
                isMobile={false}
                redirectUri={process.env.REACT_APP_HOME || ''}
                render={(renderProps) => (
                  <TextButton
                    label={t('buttons.connect')}
                    handleClick={renderProps.onClick}
                    className={TextButtonClassNames.PrimaryActive}
                  />
                )}
              />
            ),
            handleUnlink: handleDisconnectSocial,
            isConnected:
              (userInfoState?.SocialAccounts &&
                setIsSocialConnected(userInfoState.SocialAccounts, TypeOfSocials.Facebook)) ||
              false,
            email:
              (userInfoState?.SocialAccounts &&
                findSocialEmail(userInfoState.SocialAccounts, TypeOfSocials.Facebook)) ||
              false,
            type: TypeOfSocials.Facebook,
            unlinkMessage: t('snackbar.facebookAccountWasDisabled'),
          } as UserSocialAccount,
        ]
      : []),
  ];

  return (
    <div className="account__social-accounts">
      <div className="account__social-accounts__header">
        <article className="account__social-accounts__header__title">{t('accountSettings.addedAccounts')}</article>
        <p className="account__social-accounts__header__additional-text">
          {t('accountSettings.connectedAccountsQuicklyEnterPersonalAccount')}
        </p>
      </div>
      <div className="account__social-accounts__items">
        {userSocialAccounts.map((el) => {
          return (
            <div key={el.label}>
              <SocialItem {...el} />
              <hr style={{ marginTop: '25px' }} />
            </div>
          );
        })}
      </div>
    </div>
  );
};
